"use client";

import { useEffect } from "react";

// Nested from old sweateconomy 1:1
// Re-do this from scratch for the next version, don't try to support it

interface ServerData {
  data: {
    academic_research_data: {
      total_replies: number;
      average_usd: number;
      median_usd: number;
      uniq_participants_for_groups: Record<string, number>;
    };
  };
}

export default function DynChartPage() {
  useEffect(() => {
    function getServerData(url: string): Promise<ServerData> {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(JSON.parse(xhr.responseText));
              setTimeout(() => {
                const wrapper = document.querySelectorAll(
                  ".c-wrapper"
                )[0] as HTMLElement;
                const header = document.querySelectorAll(
                  ".c-header"
                )[0] as HTMLElement;
                const description = document.getElementById(
                  "chart-description"
                ) as HTMLElement | null;
                if (description) description.style.opacity = "1";
                wrapper.style.opacity = "1";
                wrapper.style.transition = "opacity 1s linear";
                header.style.opacity = "1";
              }, 100);
            } else {
              reject(new Error(xhr.statusText));
            }
          }
        };
        xhr.send();
      });
    }

    function roundUpToNearestThousandDivisibleByThree(num: number): number {
      const nextThousand = Math.ceil(num / 1000) * 1000;
      const remainder = nextThousand % 3000;
      if (remainder === 0) {
        return nextThousand;
      } else {
        return nextThousand + (3000 - remainder);
      }
    }

    function getColBelonging(value: number): number {
      if (value < 0.1) return 0;
      if (value >= 0.1 && value < 1) return 1;
      if (value >= 1 && value < 10) return 2;
      if (value >= 5 && value < 100) return 3;
      if (value >= 100 && value < 1000) return 4;
      if (value >= 1000) return 5;
      return 0; // Fallback
    }

    const keysIdx: Record<string, number> = {
      "...0.1": 0,
      "0.1...1": 1,
      "1...10": 2,
      "10...100": 3,
      "100...1000": 4,
      "1000..": 5,
    };

    const formatLineCap = (cap: number): string =>
      cap === 0 ? "0" : `${cap / 1000}K`;

    const wrapper = document.createElement("div");
    wrapper.classList.add("c-wrapper");

    const h2users = document.createElement("h2");
    h2users.classList.add("users");
    h2users.innerText = "";

    const h2median = document.createElement("h2");
    h2median.classList.add("median");
    h2median.innerText = "";

    const h2average = document.createElement("h2");
    h2average.classList.add("average");
    h2average.innerText = "";

    const root = document.createElement("div");
    root.classList.add("root");
    wrapper.appendChild(root);

    const chart = document.createElement("div");
    chart.classList.add("chart");
    root.appendChild(chart);

    const colCount = 6;
    for (let i = 0; i < colCount; i++) {
      const col = document.createElement("div");
      col.classList.add("col", `col-${i}`);
      chart.appendChild(col);
    }

    const avg = document.createElement("div");
    const avgTitle = document.createElement("span");
    const avgValue = document.createElement("span");
    avgValue.classList.add("avg-value");
    avgTitle.innerText = "Average";
    avgTitle.appendChild(avgValue);
    avgTitle.classList.add("avg-title");
    avg.appendChild(avgTitle);
    avg.classList.add("avg");
    chart.appendChild(avg);

    const med = document.createElement("div");
    const medTitle = document.createElement("span");
    const medValue = document.createElement("span");
    medValue.classList.add("med-value");
    medTitle.innerText = "Median";
    medTitle.appendChild(medValue);
    medTitle.classList.add("med-title");
    med.appendChild(medTitle);
    med.classList.add("med");
    chart.appendChild(med);

    const colsCapacity: string[] = [
      "<$0.1",
      "$0.1 - $1",
      "$1 - $10",
      "$10 - $100",
      "$100 - $1K",
      "$1K+",
    ];

    let medColBelong: number = 0;
    let avgColBelong: number = 0;
    let medium: number = 0;
    let average: number = 0;

    function offsetFormula(value: number, min: number, max: number): number {
      return (value - min) / (max - min);
    }

    function calcOffset(
      colWidth: number,
      value: number,
      colBelong: number
    ): number {
      if (colBelong === 0) return offsetFormula(value, 0, 0.099) * colWidth;
      if (colBelong === 1) return offsetFormula(value, 0.1, 0.99) * colWidth;
      if (colBelong === 2) return offsetFormula(value, 1, 9.99) * colWidth;
      if (colBelong === 3) return offsetFormula(value, 10, 99.99) * colWidth;
      if (colBelong === 4) return offsetFormula(value, 100, 999.99) * colWidth;
      if (colBelong === 5) {
        if (value < 3000) {
          return offsetFormula(value, 1000, 2999) * colWidth;
        }
        return 0.9 * colWidth;
      }
      return 0;
    }

    function getOffset(
      target: HTMLElement,
      value: number,
      colBelong: number
    ): number {
      return calcOffset(target.offsetWidth, value, colBelong);
    }

    function updateMarkerPositions(): void {
      const medColTarget = document.querySelector(
        `.col-${medColBelong}`
      ) as HTMLElement | null;
      const avgColTarget = document.querySelector(
        `.col-${avgColBelong}`
      ) as HTMLElement | null;
      const medOffset = medColTarget
        ? getOffset(medColTarget, medium, medColBelong)
        : 0;
      const avgOffset = avgColTarget
        ? getOffset(avgColTarget, average, avgColBelong)
        : 0;

      if (medColTarget) {
        const { offsetLeft: medOffsetLeft } = medColTarget;
        const med = document.querySelector(".med") as HTMLElement;
        med.style.left = `${medOffsetLeft + medOffset}px`;
      }

      if (avgColTarget) {
        const { offsetLeft: avgOffsetLeft } = avgColTarget;
        const avg = document.querySelector(".avg") as HTMLElement;
        avg.style.left = `${avgOffsetLeft + avgOffset}px`;
      }

      const avgTitle = document.querySelectorAll(
        ".avg-title"
      )[0] as HTMLElement;
      const medTitle = document.querySelectorAll(
        ".med-title"
      )[0] as HTMLElement;
      const avgPosition = avgTitle.getBoundingClientRect();
      const medPosition = medTitle.getBoundingClientRect();
      const isAvgFirst = avgPosition.left < medPosition.left;
      const isCollision =
        avgPosition.right > medPosition.left &&
        avgPosition.left < medPosition.right + 4;

      if (isCollision) {
        if (isAvgFirst) {
          avgTitle.classList.add("right-align-title");
        } else {
          medTitle.classList.add("right-align-title");
        }
      }
    }

    window.addEventListener("resize", updateMarkerPositions);

    getServerData("https://production.sweatco.in/landing/academic_research")
      .then(({ data }) => {
        const usersCountTag = document.querySelectorAll(
          "h2.users"
        )[0] as HTMLElement;
        const averageCountTag = document.querySelectorAll(
          "h2.average"
        )[0] as HTMLElement;
        const medianCountTag = document.querySelectorAll(
          "h2.median"
        )[0] as HTMLElement;

        usersCountTag.innerText =
          data.academic_research_data.total_replies.toLocaleString();
        averageCountTag.innerText = `$${data.academic_research_data.average_usd
          .toFixed(2)
          .toLocaleString()}*`;
        medianCountTag.innerText = `$${data.academic_research_data.median_usd
          .toFixed(2)
          .toLocaleString()}`;

        const maxParticipants = roundUpToNearestThousandDivisibleByThree(
          Math.max(
            ...Object.values(
              data.academic_research_data.uniq_participants_for_groups
            )
          )
        );

        const linesCap: (number | undefined)[] = [
          0,
          undefined,
          undefined,
          undefined,
        ];
        const step = maxParticipants / 3;
        linesCap[1] = linesCap[0]! + step;
        linesCap[2] = linesCap[1]! + step;
        linesCap[3] = linesCap[2]! + step;
        const formattedLinesCap = linesCap.map((cap) => formatLineCap(cap!));

        medium = data.academic_research_data.median_usd;
        medColBelong = getColBelonging(medium);
        average = data.academic_research_data.average_usd;
        avgColBelong = getColBelonging(average);

        formattedLinesCap.forEach((lineCap, i) => {
          const line = document.createElement("div");
          const label = document.createElement("span");
          label.innerText = lineCap;
          label.classList.add("line-label", "collout-s");
          line.appendChild(label);
          line.classList.add("line", `line-${i}`);
          root.appendChild(line);
        });

        Object.keys(
          data.academic_research_data.uniq_participants_for_groups
        ).forEach((key) => {
          const value =
            data.academic_research_data.uniq_participants_for_groups[key];
          const percentage = value / maxParticipants;
          const colIdx = keysIdx[key];
          const className = `.col-${colIdx}`;
          const col = document.querySelectorAll(className)[0] as HTMLElement;
          setTimeout(() => {
            col.style.height = `${percentage * 100}%`;
          }, 50);
          const description = document.createElement("span");
          description.classList.add("col-description", "collout-s");
          description.innerText = colsCapacity[colIdx];
          col.appendChild(description);
          const medValueTag = document.querySelectorAll(
            ".med-value"
          )[0] as HTMLElement;
          medValueTag.innerText = `$${medium.toFixed(2)}`;
          const avgValueTag = document.querySelectorAll(
            ".avg-value"
          )[0] as HTMLElement;
          avgValueTag.innerText = `$${average.toFixed(2)}*`;
          updateMarkerPositions();
        });
      })
      .catch((error) => {
        console.error("Data load error:", error);
      });

    const styles = `
      .c-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        max-width: 1200px;
        height: 100%;
        border-radius: 40px;
        padding: 40px 60px 36px;
        position: relative;
        opacity: 0;
        transition: opacity 200ms ease-in-out;
        margin: 0 auto;
      }
      .c-head {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 40px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        position: relative;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.10) 0%, rgba(153, 153, 153, 0.00) 65%);
      }
      .c-head::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 40px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border: 2px solid transparent;
        border-bottom: none;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0)) border-box;
        -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out;
        mask-composite: exclude;
      }
      .root {
        position: relative;
        display: inline-flex;
        align-items: flex-end;
        height: 300px;
        width: 100%;
        margin-top: 64px;
      }
      .chart {
        display: flex;
        align-items: flex-end;
        width: 100%;
        height: 100%;
        padding: 0 40px;
      }
      @media all and (max-width: 991px) {
        .chart {
          padding: 0 12px;
        }
      }
      .col {
        position: relative;
        flex: 1;
        height: 0;
        border-radius: 12px 12px 0px 0px;
        background: linear-gradient(180deg, rgba(255, 122, 226, 0.60) 0%, rgba(18, 61, 80, 0.20) 100%);
        transition: height .75s ease-in-out;
      }
      .col + .col {
        margin-left: 20px;
      }
      @media all and (max-width: 991px) {
        .col + .col {
          margin-left: 10px;
        }
      }
      @media all and (max-width: 540px) {
        .col + .col {
          margin-left: 4px;
        }
      }
      @media all and (min-width: 768px) {
        .c-wrapper {
          padding: 40px 160px 20px;
        }
      }
      .line {
        position: absolute;
        left: 0;
        width: 100%;
        height: 1px;
        border-top: 1px dashed rgba(255, 13, 117, 0.6);
      }
      .line-3 { top: 0%; }
      .line-2 { top: 33.333333%; }
      .line-1 { top: 66.66666%; }
      .line-0 { top: 100%; }
      .line-label {
        position: absolute;
        transform: translateY(-50%) translateX(-100px);
        color: #ffffff;
        text-align: right;
        width: 40px;
      }
      @media all and (max-width: 991px) {
        .line-label {
          transform: translateY(-50%) translateX(-48px);
        }
      }
      .avg,
      .med {
        position: absolute;
        bottom: 1px;
        height: 120%;
      }
      .avg-title,
      .med-title {
        font-family: Inter, sans-serif;
        display: flex;
        flex-direction: column;
        align-items: left;
        position: absolute;
        top: -2px;
        left: 8px;
        font-size: 1rem;
      }
      .right-align-title {
        left: 0;
        transform: translateX(calc(-100% - 8px));
        text-align: right;
      }
      .avg {
        border-left: 2px dashed #fff;
      }
      .avg-title {
        color: #fff;
      }
      .med {
        border-left: 2px dashed #5AEAB2;
      }
      .med-title {
        color: #5AEAB2;
      }
      .med-value,
      .avg-value {
        padding-top: 4px;
        font-weight: bold;
      }
      .collout-s {
        font-family: Inter, sans-serif;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: 16px;
      }
      .col-description {
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%) translateY(100%);
        opacity: 0.8;
        color: #ffffff;
        width: 100%;
        text-align: center;
        word-wrap: normal;
        overflow-wrap: normal;
      }
      .footnote {
        display: inline-block;
        color: rgba(255, 255, 255, 0.60);
        text-align: center;
        font-family: Inter, sans-serif;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
        padding: 0;
        margin: 0;
      }
      .title-s {
        color: rgba(255, 255, 255, 0.80);
        text-align: center;
        font-family: Inter, sans-serif;
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px;
        color: #ffffff;
        opacity: 0.8;
      }
      .collout-m {
        color: rgba(255, 255, 255, 0.80);
        text-align: center;
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
      }
      .c-header {
        display: flex;
        flex-wrap: nowrap;
        align-items: flex-end;
        justify-content: center;
        width: 80%;
        opacity: 0;
        transition: opacity 0.1s ease-in-out;
      }
      .head-estimates,
      .head-average {
        height: 60px;
        width: 240px;
        min-width: 240px;
        max-width: 240px;
      }
      .head-median {
        height: 80px;
        width: 280px;
        min-width: 280px;
        max-width: 280px;
      }
      @media all and (max-width: 901px) {
        .c-header {
          width: 100%;
          flex-wrap: wrap;
        }
        .median-wrapper {
          width: 100%;
          min-width: 100%;
          max-width: 100%;
          height: 72px;
          display: flex;
          justify-content: center;
          order: 1;
          flex-basis: 100%;
        }
        .head-median {
          max-width: 274px;
          height: 72px;
        }
        .head-estimates,
        .head-average {
          max-width: 40%;
        }
        .head-estimates {
          order: 2;
        }
        .head-average {
          order: 3;
        }
      }
      .users,
      .average {
        margin: 0;
        padding: 0;
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        text-align: center;
        color: #ffffff;
      }
      .median {
        margin: 0;
        padding: 0;
        font-family: Inter, sans-serif;
        font-size: 36px;
        font-weight: 700;
        line-height: 46px;
        text-align: center;
        color: #5AEAB2;
      }
      .line-3 > .line-label::after {
        position: absolute;
        content: 'Estimate count';
        width: 84px;
        bottom: 28px;
        right: 24px;
        transform: translateX(100%);
        z-index: 4;
        color: rgba(255, 255, 255, 0.8);
        text-align: left;
        font-family: Inter, sans-serif;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
      }
      @media all and (max-width: 560px) {
        .col-description {
          font-size: 10px;
        }
        .head-estimates,
        .head-average {
          width: auto;
          max-width: auto;
          min-width: 164px;
        }
      }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    const header = document.createElement("div");
    header.classList.add("c-header");

    const numberOfEstimates = document.createElement("div");
    const numberOfEstimatesTitle = document.createElement("h2");
    numberOfEstimatesTitle.classList.add("footnote");
    numberOfEstimatesTitle.innerText = "Number of estimates";
    numberOfEstimates.appendChild(numberOfEstimatesTitle);
    numberOfEstimates.appendChild(h2users);
    numberOfEstimates.classList.add("c-head", "head-estimates");

    const medianHead = document.createElement("div");
    const medianHeadTitle = document.createElement("h2");
    medianHeadTitle.classList.add("footnote");
    medianHeadTitle.innerText = "Median";
    medianHead.appendChild(medianHeadTitle);
    medianHead.appendChild(h2median);
    medianHead.classList.add("c-head", "head-median");

    const medianWrapper = document.createElement("div");
    medianWrapper.classList.add("median-wrapper");
    medianWrapper.appendChild(medianHead);

    const averageHead = document.createElement("div");
    const averageHeadTitle = document.createElement("h2");
    averageHeadTitle.classList.add("footnote");
    averageHeadTitle.innerText = "Average";
    averageHead.appendChild(averageHeadTitle);
    averageHead.appendChild(h2average);
    averageHead.classList.add("c-head", "head-average");

    header.appendChild(numberOfEstimates);
    header.appendChild(medianWrapper);
    header.appendChild(averageHead);

    const page = document.getElementById("dyn-chart") as HTMLElement;
    page.appendChild(header);
    page.appendChild(wrapper);

    return () => {
      window.removeEventListener("resize", updateMarkerPositions);
      page.innerHTML = "";
    };
  }, []);

  return (
    <div
      id="dyn-chart"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "80px",
        marginBottom: "40px",
        width: "100vw",
        maxWidth: "1300px",
      }}
    />
  );
}
